/* ===== world/fluid.ts — 유체: 칸마다 양(0~255)을 보존하며 흐르는 물 · 바닷물 · 용암 · 잿물 ===== */
import { mixin } from '../../engine/core/mixin.js';
import { CellQueue } from '../../engine/tilemap/cellqueue.js';
import { T } from '../data.js';
import { FLUID_FLOW, FLUID_FULL, FLUID_KIND, FLUID_OPEN, FLUID_PLANT, FLUID_SOAK, FLUID_SPEC, FLUID_TILE } from '../data/materials.js';
import { World, inSeaZone } from '../world.js';
/* ★ 물은 **양을 보존한다**(점검판 2026-11-03 "물이 장식에 불과하다 — v1.2 물 공장을 대비해 사실적으로").
   가득 찬 칸(255)은 원천 타일(고인 물 · 바닷물 · 용암 · 잿물) 그대로, 덜 찬 칸은 흐르는 타일 + `liq[k]`. 원천도 양의 일부라
   호수 밑을 파면 수위가 실제로 내려가고, 양동이로 뜬 만큼 물이 줄어든다. 예외 둘 — 샘 바위는 물을 내고, 해수면 아래 바다는 끝없는 저수지다.
   한 걸음: ① 아래로 먼저(받을 수 있는 만큼) ② 밑이 받쳐지면 옆과 나눈다(액체마다 나누는 몫 · 끈적임) ③ 얇게 남으면 마른다(용암은 식어 현무암).
   잿물은 물보다 무거워 가라앉고(칸 맞바꿈) 물에 닿으면 조금씩 녹아 그 물을 흐리게 한다(`murk`). 용암이 물 · 잿물에 닿으면 굳는다.
   ★ 생성 중에도 돌므로(fluidSettle) 난수는 Math.random 이 아니라 칸 · 걸음 정수 해시 — 브라우저마다 세계가 달라지면 안 된다(#h186). 사연: docs/code-history.md#h264 */

const FULL = 255;

export const WorldFluid: Bag & ThisType<World> = {
  /** 이 칸의 양 0~255 — 원천 255 · 흐르는 칸 liq · 액체가 아니면 0 */
  amtAt(k: number) {
    const t = this.tiles[k];
    if (!FLUID_KIND[t]) return 0;
    return FLUID_FLOW[t] || t === T.FALLS ? this.liq[k] : FULL;
  },
  /** 잠긴 비율 0~1(헤엄 · 그림) */
  liqFrac(k: number) { return this.amtAt(k) / FULL; },

  fluidInit() { const { WW, WH } = this.dims;
    this.liq = new Uint8Array(WW * WH);
    this.murk = new Uint8Array(WW * WH);
    this.fallF = new Uint8Array(WW * WH);
    this.fq = new CellQueue(WW, WH, [0.08, 0.5]);       // [물 · 바닷물 · 잿물, 용암] — 용암은 끈적해 느리다
    this.fstep = 0; this.murkSet = new Set();
    this.hangQ = [];
    const saved = this._liqLoad as number[] | null;
    if (saved) for (let i = 0; i + 1 < saved.length; i += 2) this.liq[saved[i]] = saved[i + 1];
    this._liqLoad = null;
    for (let k = WW; k < WW * (WH - 1); k++) {
      const t = this.tiles[k];
      if (t === T.SPRING) { this.fluidWake(k % WW, (k / WW) | 0); continue; }
      if (!FLUID_FLOW[t] && t !== T.FALLS) continue;
      if (!this.liq[k]) this.liq[k] = FLUID_KIND[this.tiles[k - WW]] ? FULL : 200;   // 양이 안 실린 옛 세이브 — 위가 물이면 가득
      this.fluidWake(k % WW, (k / WW) | 0);
    }
  },
  fluidWake(x: number, y: number) { this.fq!.wake(x, y); },
  fluidTick(dt: number) { if (this.fq) this.fq.tick(dt, (j: number) => this._fluidStep(j)); },

  /** 정수 해시 0~1 — 칸과 걸음으로만(생성이 브라우저마다 같게) */
  _frnd(k: number) {
    let h = (k * 374761393 + this.fstep * 668265263) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  },

  _fluidStep(j: number) {
    const { WW, WH } = this.dims;
    if (!this._fD || this._fD.length !== WW * WH) { this._fD = new Int16Array(WW * WH); this._fK = new Uint8Array(WW * WH); this._fT = new Uint8Array(WW * WH); }
    this.fstep = (this.fstep + 1) | 0;
    this._fTouch = []; this._fSet = []; this._fFall = [];
    this.fq!.step(j, (k: number) => { this._flowCell(k, j); return null; }, () => this._fluidCommit(), j === 0 ? 9000 : 4000);
    if (j === 0 && this.murkSet.size) this._murkStep();
  },
  /** 지금(이 걸음 안에서 옮긴 것까지) 양 */
  _fcur(k: number) { return this.amtAt(k) + this._fD[k]; },
  /** a → b 로 m 만큼(b 가 빈칸이면 a 의 종류로 채운다) */
  _fmove(a: number, b: number, m: number, kind: number) {
    if (m <= 0) return;
    const D = this._fD, T0 = this._fT;
    D[a] -= m; D[b] += m;
    if (!T0[a]) { T0[a] = 1; this._fTouch.push(a); }
    if (!T0[b]) { T0[b] = 1; this._fTouch.push(b); }
    if (!FLUID_KIND[this.tiles[b]]) this._fK[b] = kind;
  },

  _flowCell(k: number, j: number) {
    const { WW } = this.dims, t = this.tiles[k];
    if (t === T.SPRING) { if (j === 0) this._spring(k); return; }
    const K = FLUID_KIND[t];
    if (!K || (K === 3) !== (j === 1) || FLUID_PLANT[t]) return;
    let a = this._fcur(k);
    if (a <= 0) return;
    const x = k % WW, y = (k / WW) | 0;
    /* 해수면 아래 바다 — 끝없는 저수지(물가로 번진 물이 증발해도 바다가 줄지 않는다) */
    if (K === 2 && this.sea && y >= this.sea.level && inSeaZone(x, this.sea.x1) && a < FULL) { this._fD[k] += FULL - a; this._ftouch(k); a = FULL; }
    /* 섞임 · 굳음 */
    if (K === 3) {
      for (const d of [-1, 1, -WW, WW]) {
        const nk = FLUID_KIND[this.tiles[k + d]];
        if (nk && nk !== 3) { this._fSet.push([k, a >= 128 ? T.OBSIDIAN : T.BASALT, 0, 1]); return; }
      }
    }
    if (K === 4) {
      const bt = this.tiles[k + WW], bk = FLUID_KIND[bt];
      if ((bk === 1 || bk === 2) && !FLUID_PLANT[bt]) {        // 무거운 잿물이 물 밑으로 가라앉는다 — 두 칸을 맞바꾼다
        const ba = this._fcur(k + WW);
        this._fSet.push([k, ba >= FULL ? FLUID_FULL[bk] : FLUID_TILE[bk], ba, 0], [k + WW, a >= FULL ? T.SLUDGE : T.FLOWSLUDGE, a, 0]);
        return;
      }
      for (const d of [-1, 1, -WW]) if (FLUID_KIND[this.tiles[k + d]] === 1 && this._frnd(k + d) < 0.035) {   // 물에 닿은 잿물은 조금씩 녹아 그 물을 흐린다
        this._fSet.push([k, a >= FULL ? T.WATER : T.FLOWWATER, a, 0]); this.murk[k] = 230; this.murkSet.add(k); return;
      }
    }
    const S = FLUID_SPEC[K];
    let moved = false;
    /* ① 아래로 */
    const b = k + WW, bt = this.tiles[b], bk = FLUID_KIND[bt];
    let held = true;
    if (FLUID_OPEN(bt) || (bk === K && !FLUID_PLANT[bt])) {
      const room = FULL - this._fcur(b);
      if (room > 0) {
        const m = Math.min(a, room, S.down);
        this._fmove(k, b, m, K); a -= m; moved = true;
        if (K === 1) this._fFall.push(k);
      }
      held = this._fcur(b) >= FULL;
    }
    if (a <= 0) return;
    /* ★ 떨어지는 물줄기(폭포 · 위에서 막 받은 칸, 양옆은 마른)가 가득 찬 웅덩이에 꽂힌 자리 — 넘친 만큼은 웅덩이의 물길로 빠진 것으로 친다(지운다). 안 지우면 샘이 웅덩이를 한 줄씩 끝없이
       불려 물가 · 수련을 덮었다(낚시터 시험장). 폭포는 계속 흐르고 웅덩이 수위는 그대로 */
    if (K === 1 && held && bk === K && (t === T.FALLS || ((this.fstep - this.fallF[k - WW]) & 255) < 6)) {
      const sk = (n: number) => { const q = FLUID_KIND[this.tiles[n]]; return q === 1 || q === 2; };
      if (t === T.FALLS || (!sk(k - 1) && !sk(k + 1))) { this._fD[k] -= a; this._ftouch(k); this._fFall.push(k); return; }
    }
    /* ② 옆으로 — 밑이 받쳐졌을 때만. 걸음마다 먼저 보는 쪽을 바꾼다(줄 순서로 한쪽에 쏠리지 않게) */
    if (held) {
      const dirs = this.fstep & 1 ? [-1, 1] : [1, -1];
      for (const s of dirs) {
        const n = k + s, nt = this.tiles[n], nk = FLUID_KIND[nt];
        if (!(FLUID_OPEN(nt) || (nk === K && !FLUID_PLANT[nt]))) continue;
        const diff = a - this._fcur(n);
        if (diff < 2) continue;
        let m = Math.max(1, Math.floor(diff * S.rate * 0.5));
        if (S.visc) m = Math.min(m, a - S.min);                // 용암 — 두께가 min 아래로 얇아지며 번지지 않는다(끈적한 혓바닥)
        if (m <= 0) continue;
        this._fmove(k, n, m, K); a -= m; moved = true;
      }
      /* 스밈 — 흙 · 모래 위의 덜 찬 민물은 땅으로 빠진다(가득 찬 호수 바닥은 그대로) */
      if (K === 1 && a < FULL && FLUID_SOAK[bt] && this._frnd(k ^ 0x5bd1) < 0.6) { this._fD[k] -= 1; a -= 1; this._ftouch(k); }
      /* ③ 얇게 남은 것 — 물은 마르고, 용암은 식어 현무암, 잿물은 재로 가라앉는다 */
      if (!moved && a < S.min) {
        const r = this._frnd(k);
        if (K === 3) { if (r < 0.06) this._fSet.push([k, T.BASALT, 0, 1]); else this._fwake(k); }
        else if (r < S.dry) { this._fD[k] -= a; this._ftouch(k); }
        else this._fwake(k);
      }
    }
    /* 잿물 — 공기에 드러난 윗면은 재가 가라앉으며 줄어든다(보스가 쏟은 범람이 영영 남지 않게) */
    if (K === 4 && FLUID_OPEN(this.tiles[k - WW]) && a > 0) { const m = Math.min(a, 2); this._fD[k] -= m; this._ftouch(k); }
  },
  _ftouch(k: number) { if (!this._fT[k]) { this._fT[k] = 1; this._fTouch.push(k); } },
  _fwake(k: number) { const { WW } = this.dims; this.fq!.wake(k % WW, (k / WW) | 0); },

  /** 샘 바위 — 밑(막혔으면 옆)으로 물을 낸다. 받을 데가 차면 쉬고, 물이 빠지면 set 이 다시 깨운다 */
  _spring(k: number) {
    const { WW } = this.dims;
    for (const d of [WW, -1, 1]) {
      const n = k + d, nt = this.tiles[n];
      if (!(FLUID_OPEN(nt) || nt === T.WATER || nt === T.FLOWWATER || nt === T.FALLS)) continue;
      const room = FULL - this._fcur(n);
      if (room <= 0) continue;
      this._fD[n] += Math.min(room, 28); this._ftouch(n);   // 샘 하나 ≈ 초당 1.4칸 — 흙에 스미는 만큼과 맞선다
      if (!FLUID_KIND[nt]) this._fK[n] = 1;
      if (d === WW) this._fFall.push(n);
      this._fwake(k);
      return;
    }
  },

  /** 다 잰 뒤 한꺼번에 — 양을 타일 · liq 로 옮겨 적는다 */
  _fluidCommit() {
    const { WW } = this.dims, D = this._fD, KI = this._fK, TT = this._fT;
    const cols = new Set<number>();
    for (const k of this._fTouch) {
      const t = this.tiles[k], K = FLUID_KIND[t] || KI[k], v = this.amtAt(k) + D[k];
      D[k] = 0; KI[k] = 0; TT[k] = 0;
      if (!K) continue;
      const x = k % WW, y = (k / WW) | 0;
      let nt: number, lv = 0;
      if (v <= 0) nt = T.AIR;
      else if (v >= FULL && t !== T.FALLS) nt = FLUID_FULL[K];
      else { nt = t === T.FALLS && K === 1 ? T.FALLS : FLUID_TILE[K]; lv = Math.min(FULL, v); }
      if (nt === T.AIR && !FLUID_KIND[t]) continue;
      if (nt !== t) this.set(x, y, nt); else { this.fluidWake(x, y); this.netMark(k); }
      this.liq[k] = lv;
      if (K === 1) cols.add(k);
    }
    for (const [k, nt, lv, solid] of this._fSet) {
      const x = k % WW, y = (k / WW) | 0;
      this.set(x, y, nt); this.liq[k] = FLUID_FLOW[nt] ? Math.max(1, Math.min(FULL, lv)) : 0;
      if (solid && this.fxQ && this.fxQ.length < 200) this.fxQ.push([x, y, 1]);   // 김 · 지글거림(게임이 그린다)
      if (FLUID_KIND[nt] === 1) cols.add(k);
    }
    for (const k of this._fFall) this.fallF[k] = this.fstep & 255;
    for (const k of cols) this._fallsCol(k % WW, (k / WW) | 0);
  },

  /** 흐린 물 — 둘레 물과 섞이며 옅어진다(그림만 · 저장 안 함) */
  _murkStep() {
    const { WW } = this.dims, M = this.murk;
    for (const k of [...this.murkSet]) {
      let v = M[k];
      if (!FLUID_KIND[this.tiles[k]]) { M[k] = 0; this.murkSet.delete(k); continue; }
      for (const d of [-1, 1, -WW, WW]) {
        const n = k + d;
        if (FLUID_KIND[this.tiles[n]] !== 1 || M[n] >= v - 12) continue;
        const m = (v - M[n]) >> 3; M[n] += m; v -= m; this.murkSet.add(n);
      }
      v = Math.max(0, v - 1); M[k] = v;
      if (!v) this.murkSet.delete(k);
    }
  },

  /** (x, y) 에 액체를 붓는다 — 양동이 · 보스 범람. 넘친 양은 위 칸으로 쌓는다. 실제로 부은 양을 돌려준다 */
  pourFluid(x: number, y: number, kind: number, amount: number) {
    const { WW } = this.dims;
    let left = amount, k = y * WW + x;
    for (let i = 0; i < 6 && left > 0 && this.inB(x, y - i); i++, k -= WW) {
      const t = this.tiles[k], K = FLUID_KIND[t];
      if (!(FLUID_OPEN(t) || (K === kind && !FLUID_PLANT[t]))) break;
      const room = FULL - this.amtAt(k), m = Math.min(room, left);
      if (m <= 0) continue;
      const v = this.amtAt(k) + m;
      this.set(x, y - i, v >= FULL ? FLUID_FULL[kind] : FLUID_TILE[kind]);
      this.liq[k] = v >= FULL ? 0 : v;
      left -= m;
    }
    return amount - left;
  },

  /** ★ 폭포 판정 — 지금 물을 아래로 넘기고 있는(fallF) 민물 줄기 가운데 4칸 이상 곧게 떨어지고 양옆에 물이 없는 토막만 폭포(FALLS) — 사연: #h135 · #h264 */
  _fallsCol(x: number, y: number) { const { WW, WH } = this.dims;
    const now = this.fstep & 255;
    const falling = (k: number) => {
      const t = this.tiles[k];
      if (t !== T.FALLS && t !== T.FLOWWATER && t !== T.WATER) return false;
      return ((now - this.fallF[k]) & 255) < 6;
    };
    let k = y * WW + x;
    if (!falling(k)) { if (this.tiles[k] === T.FALLS) this._fallsSet(k, false); return; }
    let top = k, bot = k;
    while (top - WW > WW && falling(top - WW)) top -= WW;
    while (bot + WW < WW * (WH - 1) && falling(bot + WW)) bot += WW;
    const wetSide = (n: number) => { const kd = FLUID_KIND[this.tiles[n]]; return kd === 1 || kd === 2; };
    let seg: number[] = [];
    const flush = () => { for (const n of seg) this._fallsSet(n, seg.length >= 4); seg = []; };
    for (let n = top; n <= bot; n += WW) {
      if (wetSide(n - 1) || wetSide(n + 1)) { flush(); this._fallsSet(n, false); }
      else seg.push(n);
    }
    flush();
  },
  _fallsSet(n: number, on: boolean) {
    const { WW } = this.dims, t = this.tiles[n], a = this.amtAt(n);
    const want = on ? T.FALLS : a >= FULL ? T.WATER : T.FLOWWATER;
    if (t === want) return;
    this.set(n % WW, (n / WW) | 0, want); this.liq[n] = want === T.WATER ? 0 : Math.max(1, a);
  },
  fallsAll() { const { WW, WH } = this.dims;
    for (let k = WW; k < WW * (WH - 1); k++) if (this.tiles[k] === T.FALLS && this.tiles[k - WW] !== T.FALLS) this._fallsCol(k % WW, (k / WW) | 0);
  },

  /** 세계를 막 만들었을 때 — 샘 · 폭포 · 물길이 자리 잡을 때까지 미리 돌린다 */
  fluidSettle() {
    const q = this.fq!;
    for (let i = 0; i < 700 && q.busy(); i++) {
      if (q.busy(0)) this._fluidStep(0);
      if (i % 5 === 0 && q.busy(1)) this._fluidStep(1);
    }
  },

  /** 물살 방향과 세기(-1~1) — 양이 많은 쪽에서 적은 쪽으로 */
  currentAt(x: number, y: number) { const { WW } = this.dims;
    const k = y * WW + x, t = this.tiles[k], K = FLUID_KIND[t];
    if (!FLUID_FLOW[t]) return 0;
    const me = this.amtAt(k);
    const side = (n: number) => { const nt = this.tiles[n]; return FLUID_KIND[nt] === K ? this.amtAt(n) : FLUID_OPEN(nt) ? 0 : me; };
    return Math.max(-1, Math.min(1, (side(k - 1) - side(k + 1)) / 64));
  },

  /** 저장 — 덜 찬 칸만 [칸, 양] 줄로(원천은 타일이 곧 양이다) */
  liqSave() {
    const out: number[] = [], L = this.liq; if (!L) return out;
    for (let k = 0; k < L.length; k++) if (L[k] && (FLUID_FLOW[this.tiles[k]] || this.tiles[k] === T.FALLS)) out.push(k, L[k]);
    return out;
  },
};
mixin(World.prototype, WorldFluid, true);
